/**
 * Open Account Reward Optimizer Engine (Svelte Client)
 * Solves the Multiple-Choice Knapsack problem across bank account opening promotions
 * to maximize welcome rewards for new money deposits without installments.
 */

/**
 * Parses condition strings e.g. "HK$2,000,000", "$1,000,000 - $1,999,999", "$200,000+"
 * @param {string} condition
 * @returns {{min: number, max: number}}
 */
export function parseConditionRange(condition) {
  if (!condition) return { min: 0, max: 99999999 };
  const str = String(condition).trim();
  const cleaned = str.replace(/,/g, '');
  const matches = cleaned.match(/\d+(?:\.\d+)?/g);
  if (!matches || matches.length === 0) return { min: 0, max: 99999999 };

  const nums = matches.map(Number);
  if (nums.length >= 2) {
    return { min: Math.min(nums[0], nums[1]), max: Math.max(nums[0], nums[1]) };
  }

  const lower = cleaned.toLowerCase();
  const isBelow = lower.includes('under') ||
    lower.includes('below') ||
    lower.includes('<') ||
    lower.includes('以下') ||
    lower.includes('up to');

  if (isBelow) {
    return { min: 0, max: nums[0] };
  }
  return { min: nums[0], max: 99999999 };
}

/**
 * Normalizes raw tier into structured tier object
 * @param {Object} tier
 * @returns {Object}
 */
export function parseRewardTier(tier) {
  const condition = String(tier.condition || '');
  const range = parseConditionRange(condition);
  const rType = String(tier.rewardType || '').toLowerCase() === 'percentage' ? 'Percentage' : 'Fixed';
  const fixed = Number(tier.fixedAmount) || Number(tier.topupAmount) || 0;
  const pct = Number(tier.percentage) || 0;
  const maxR = Number(tier.maxRewardAmount) || 0;
  const uom = String(tier.unitOfMeasure || '$');

  return {
    minDeposit: range.min > 0 ? range.min : (Number(tier.minimumSpending) || 0),
    maxDeposit: range.max,
    rewardType: rType,
    fixedReward: fixed,
    percentage: pct,
    maxRewardAmount: maxR,
    unitOfMeasure: uom,
    conditionText: condition,
  };
}

/**
 * Derives normalized bank group for mutual exclusivity
 * @param {Object} promo
 * @returns {string}
 */
export function getBankGroup(promo) {
  if (!promo) return 'Other';
  const text = `${promo.brand || ''} ${promo.name || ''} ${(promo.banks || []).join(' ')}`.toUpperCase();

  if (text.includes('HSBC') || text.includes('滙豐') || text.includes('汇丰')) return 'HSBC';
  if (text.includes('BOC') || text.includes('中銀') || text.includes('中银') || text.includes('BANK OF CHINA')) return 'BOC';
  if (text.includes('STANDARD CHARTERED') || text.includes('渣打')) return 'Standard Chartered';
  if (text.includes('CITI') || text.includes('花旗')) return 'Citibank';
  if (text.includes('HANG SENG') || text.includes('恒生')) return 'Hang Seng';
  if (text.includes('BEA') || text.includes('東亞') || text.includes('EAST ASIA')) return 'BEA';
  if (text.includes('DBS') || text.includes('星展')) return 'DBS';
  if (text.includes('CCB') || text.includes('建行') || text.includes('CONSTRUCTION')) return 'CCB Asia';
  if (text.includes('ZA BANK') || text.includes('眾安')) return 'ZA Bank';
  if (text.includes('MOX')) return 'Mox Bank';

  if (Array.isArray(promo.banks) && promo.banks.length > 0) {
    return promo.banks[0];
  }
  return promo.brand || 'Other';
}

/**
 * Normalizes promotion object from API / database
 * @param {Object} raw
 * @returns {Object}
 */
export function normalizeOpenAccountPromotion(raw) {
  const id = raw._id || raw.id || Math.random().toString(36).substring(2);
  const name = raw.name || '';
  const desc = raw.description || '';
  const banks = Array.isArray(raw.banks) ? raw.banks.map(String) : [];

  let brand = raw.brand || '';
  if (!brand && banks.length > 0) brand = banks[0];
  if (!brand) {
    const upper = `${name.toUpperCase()} ${desc.toUpperCase()}`;
    if (upper.includes('HSBC') || upper.includes('滙豐')) brand = 'HSBC';
    else if (upper.includes('BOC') || upper.includes('中銀')) brand = 'BOC';
    else if (upper.includes('STANDARD CHARTERED') || upper.includes('渣打')) brand = 'Standard Chartered';
    else if (upper.includes('CITI') || upper.includes('花旗')) brand = 'Citibank';
    else brand = 'Bank';
  }

  const bankGroup = getBankGroup({ brand, name, banks });

  let unit = '$';
  let isFixed = false;
  let fixedAmt = 0;
  let mult = 0;
  let maxReward = Number(raw.maximumReward) || Number(raw.maxRewardAmount) || 0;
  let minDep = Number(raw.minimumSpending) || Number(raw.minimumDeposit) || 0;
  const remarks = raw.remarks || '';

  const parsedTiers = [];

  if (Array.isArray(raw.rewardTiers) && raw.rewardTiers.length > 0) {
    for (const t of raw.rewardTiers) {
      parsedTiers.push(parseRewardTier(t));
    }
    parsedTiers.sort((a, b) => a.minDeposit - b.minDeposit);

    for (const tier of parsedTiers) {
      const uom = String(tier.unitOfMeasure).toLowerCase();
      if (uom.includes('mile') || uom.includes('里')) {
        unit = 'miles';
      }
    }

    if (parsedTiers.length > 0) {
      if (minDep === 0) minDep = parsedTiers[0].minDeposit;
      if (parsedTiers[0].rewardType === 'Fixed' && parsedTiers[0].fixedReward > 0) {
        isFixed = true;
        fixedAmt = parsedTiers[0].fixedReward;
      }
    }
  } else {
    const rewardTypeStr = String(raw.rewardType || '').toLowerCase();
    const rewAmt = Number(raw.rewardAmount) || 0;
    const uom = String(raw.unitOfMeasure || '$').toLowerCase();

    if (uom.includes('mile') || uom.includes('里')) {
      unit = 'miles';
    }

    if (rewardTypeStr === 'percentage' || rewardTypeStr === 'percent') {
      mult = rewAmt;
    } else {
      isFixed = true;
      fixedAmt = rewAmt;
    }

    if (minDep > 0) {
      parsedTiers.push({
        minDeposit: minDep,
        maxDeposit: 99999999,
        rewardType: isFixed ? 'Fixed' : 'Percentage',
        fixedReward: fixedAmt,
        percentage: mult,
        maxRewardAmount: maxReward,
        unitOfMeasure: unit,
        conditionText: ''
      });
    }
  }

  return {
    id,
    brand,
    bankGroup,
    name,
    description: desc,
    unit,
    isFixedReward: isFixed,
    fixedRewardAmount: fixedAmt,
    multiplier: mult,
    maxRewardAmount: maxReward,
    minDeposit: minDep,
    tiers: parsedTiers,
    banks,
    remarks
  };
}

/**
 * Evaluates reward earned for a specific deposit amount in a promo
 * @param {Object} promo
 * @param {number} amount
 * @returns {{reward: number, remarks: string, qualifyingDeposit: number}}
 */
export function evaluateDeposit(promo, amount) {
  if (amount <= 0) {
    return { reward: 0, remarks: '', qualifyingDeposit: 0 };
  }

  if (Array.isArray(promo.tiers) && promo.tiers.length > 0) {
    let bestTier = null;
    for (const tier of promo.tiers) {
      if (amount >= tier.minDeposit) {
        if (!bestTier || tier.minDeposit >= bestTier.minDeposit) {
          bestTier = tier;
        }
      }
    }

    if (bestTier) {
      if (bestTier.rewardType === 'Percentage' && bestTier.percentage > 0) {
        const effectiveDeposit = Math.min(amount, bestTier.maxDeposit);
        let rawReward = effectiveDeposit * (bestTier.percentage / 100);
        if (bestTier.maxRewardAmount > 0 && rawReward > bestTier.maxRewardAmount) {
          rawReward = bestTier.maxRewardAmount;
        }
        const isCapped = bestTier.maxRewardAmount > 0 && rawReward >= bestTier.maxRewardAmount;
        return {
          reward: Math.round(rawReward * 100) / 100,
          remarks: `${bestTier.percentage}% reward${isCapped ? ` (Capped at HK$${bestTier.maxRewardAmount})` : ''}`,
          qualifyingDeposit: bestTier.minDeposit
        };
      } else {
        return {
          reward: bestTier.fixedReward,
          remarks: bestTier.conditionText || `Deposit HK$${bestTier.minDeposit.toLocaleString()}`,
          qualifyingDeposit: bestTier.minDeposit
        };
      }
    } else {
      return {
        reward: 0,
        remarks: `Below minimum deposit HK$${promo.minDeposit.toLocaleString()}`,
        qualifyingDeposit: 0
      };
    }
  }

  if (promo.isFixedReward) {
    if (amount >= promo.minDeposit) {
      return {
        reward: promo.fixedRewardAmount,
        remarks: `Deposit HK$${promo.minDeposit.toLocaleString()}`,
        qualifyingDeposit: promo.minDeposit
      };
    }
    return {
      reward: 0,
      remarks: `Below minimum deposit HK$${promo.minDeposit.toLocaleString()}`,
      qualifyingDeposit: 0
    };
  }

  if (promo.multiplier > 0) {
    if (amount >= promo.minDeposit) {
      let rawReward = amount * (promo.multiplier / 100);
      if (promo.maxRewardAmount > 0 && rawReward > promo.maxRewardAmount) {
        rawReward = promo.maxRewardAmount;
      }
      return {
        reward: Math.round(rawReward * 100) / 100,
        remarks: `${promo.multiplier}% reward`,
        qualifyingDeposit: promo.minDeposit
      };
    }
  }

  return { reward: 0, remarks: '', qualifyingDeposit: 0 };
}

/**
 * Calculates the optimal mix of Open Account promotions
 * @param {Object} options
 * @param {number} options.totalNewMoney
 * @param {Array<Object>} options.selectedPromotions
 * @param {boolean} [options.crossPromotion=true]
 * @param {string} [options.preference='cash']
 * @returns {Object}
 */
export function calculateOpenAccountReward({
  totalNewMoney,
  selectedPromotions,
  crossPromotion = true,
  preference = 'cash'
}) {
  const preferredUnit = preference === 'miles' ? 'miles' : '$';
  const warnings = [];

  const eligible = (selectedPromotions || []).filter(p => p.unit === preferredUnit);

  // Check same bank multiple selections
  const bankCounts = new Map();
  for (const p of eligible) {
    bankCounts.set(p.bankGroup, (bankCounts.get(p.bankGroup) || 0) + 1);
  }
  for (const [bank, count] of bankCounts.entries()) {
    if (count > 1) {
      warnings.push(`Only 1 account tier can be opened per bank (${bank}).`);
    }
  }

  // Single promotion evaluations
  const singleResults = [];
  for (const p of eligible) {
    const evalRes = evaluateDeposit(p, totalNewMoney);
    singleResults.push({
      promotionId: p.id,
      brand: p.brand,
      promotionName: p.name,
      depositAmount: evalRes.reward > 0 ? totalNewMoney : 0,
      rewardEarned: evalRes.reward,
      unit: p.unit,
      remarks: evalRes.remarks
    });
  }
  singleResults.sort((a, b) => b.rewardEarned - a.rewardEarned);

  const bestSingleReward = singleResults.length > 0 ? singleResults[0].rewardEarned : 0;

  if (!crossPromotion) {
    const bestSingle = singleResults.length > 0 ? singleResults[0] : null;
    const allocated = (bestSingle && bestSingle.rewardEarned > 0) ? totalNewMoney : 0;
    const allocs = [];
    if (bestSingle && bestSingle.rewardEarned > 0) {
      allocs.push({
        promotionId: bestSingle.promotionId,
        brand: bestSingle.brand,
        promotionName: bestSingle.promotionName,
        amountDeposited: allocated,
        rewardEarned: bestSingle.rewardEarned,
        unit: bestSingle.unit,
        remarks: bestSingle.remarks
      });
    }

    return {
      totalNewMoney,
      totalAllocated: allocated,
      remainingUnallocated: Math.max(0, totalNewMoney - allocated),
      grandTotalReward: bestSingle ? bestSingle.rewardEarned : 0,
      preference,
      unit: preferredUnit,
      isCrossPromotion: false,
      allocations: allocs,
      singlePromotionResults: singleResults,
      bestSingleReward,
      gainVsBestSingle: 0,
      percentGainVsBestSingle: 0,
      calculationDate: new Date(),
      warnings
    };
  }

  // Multiple-Choice Knapsack by bank group
  const byBank = new Map();
  for (const p of eligible) {
    if (!byBank.has(p.bankGroup)) byBank.set(p.bankGroup, []);
    byBank.get(p.bankGroup).push(p);
  }

  const bankChoices = [];
  for (const [, promos] of byBank.entries()) {
    const choices = [{ promo: null, deposit: 0, reward: 0, remarks: '' }];

    for (const p of promos) {
      const candidateDeposits = new Set();
      if (Array.isArray(p.tiers) && p.tiers.length > 0) {
        for (const t of p.tiers) {
          if (t.minDeposit <= totalNewMoney && t.minDeposit > 0) {
            candidateDeposits.add(t.minDeposit);
          }
        }
      } else if (p.minDeposit > 0 && p.minDeposit <= totalNewMoney) {
        candidateDeposits.add(p.minDeposit);
      }

      for (const dep of candidateDeposits) {
        const evalRes = evaluateDeposit(p, dep);
        if (evalRes.reward > 0) {
          choices.push({
            promo: p,
            deposit: dep,
            reward: evalRes.reward,
            remarks: evalRes.remarks
          });
        }
      }
    }

    choices.sort((a, b) => a.deposit - b.deposit);
    bankChoices.push(choices);
  }

  let bestTotalReward = -1;
  let bestTotalAllocated = 0;
  let bestCombination = [];

  const suffixMaxReward = new Array(bankChoices.length + 1).fill(0);
  for (let i = bankChoices.length - 1; i >= 0; i--) {
    let maxChoice = 0;
    for (const c of bankChoices[i]) {
      if (c.reward > maxChoice) maxChoice = c.reward;
    }
    suffixMaxReward[i] = suffixMaxReward[i + 1] + maxChoice;
  }

  function dfs(bankIndex, currentSpend, currentReward, currentPicks) {
    if (currentSpend > totalNewMoney) return;

    if (currentReward + suffixMaxReward[bankIndex] < bestTotalReward) {
      return;
    }

    if (bankIndex === bankChoices.length) {
      if (
        currentReward > bestTotalReward ||
        (currentReward === bestTotalReward && currentSpend > bestTotalAllocated) ||
        (currentReward === bestTotalReward && currentSpend === bestTotalAllocated && currentPicks.length < bestCombination.length)
      ) {
        bestTotalReward = currentReward;
        bestTotalAllocated = currentSpend;
        bestCombination = currentPicks.slice();
      }
      return;
    }

    for (const choice of bankChoices[bankIndex]) {
      if (currentSpend + choice.deposit > totalNewMoney) continue;

      if (choice.promo && choice.reward > 0) {
        currentPicks.push(choice);
        dfs(bankIndex + 1, currentSpend + choice.deposit, currentReward + choice.reward, currentPicks);
        currentPicks.pop();
      } else {
        dfs(bankIndex + 1, currentSpend, currentReward, currentPicks);
      }
    }
  }

  dfs(0, 0, 0, []);

  const finalAllocations = bestCombination.map(pick => ({
    promotionId: pick.promo.id,
    brand: pick.promo.brand,
    promotionName: pick.promo.name,
    amountDeposited: pick.deposit,
    rewardEarned: pick.reward,
    unit: pick.promo.unit,
    remarks: pick.remarks
  }));

  finalAllocations.sort((a, b) => b.amountDeposited - a.amountDeposited);

  const grandReward = bestTotalReward >= 0 ? bestTotalReward : 0;
  const allocatedMoney = bestTotalReward >= 0 ? bestTotalAllocated : 0;
  const gain = Math.max(0, grandReward - bestSingleReward);
  const percentGain = bestSingleReward > 0 ? (gain / bestSingleReward) * 100 : 0;

  return {
    totalNewMoney,
    totalAllocated: allocatedMoney,
    remainingUnallocated: Math.max(0, totalNewMoney - allocatedMoney),
    grandTotalReward: Math.round(grandReward * 100) / 100,
    preference,
    unit: preferredUnit,
    isCrossPromotion: true,
    allocations: finalAllocations,
    singlePromotionResults: singleResults,
    bestSingleReward,
    gainVsBestSingle: Math.round(gain * 100) / 100,
    percentGainVsBestSingle: Math.round(percentGain * 10) / 10,
    calculationDate: new Date(),
    warnings
  };
}
